import { useRef, useState } from 'react'

import { MatterBriefcase, Offline, TrashDelete } from '@/assets/icons'
import { Modal, ModalDescription, ModalTitle } from '@/components/modal/Modal'
import { ModalContainer } from '@/components/modal/ModalContainer'
import { ModalFooterWithActions } from '@/components/modal/ModalFooterWithActions'
import { ModalFooterWithActionsType } from '@/components/modal/constants'

export interface RemovalModalProps {
  /** Email of the member being removed; shown as the subtitle and typed to confirm. */
  memberEmail: string
  loading: boolean
  error: string | null
  onConfirm: () => void
  onClose: () => void
  /** Raise this modal above another open modal (e.g. the member roster). Default: false. */
  stacked?: boolean
}

/**
 * Destructive confirmation shown before removing a member from the organization. Owns the
 * type-the-email-to-confirm gate; the container ({@link ./RemovalDialogContainer}) owns the
 * mutation, loading/error state, success toast, and roster refresh.
 */
export function RemovalModal({ memberEmail, loading, error, onConfirm, onClose, stacked = false }: RemovalModalProps) {
  const [typedEmail, setTypedEmail] = useState('')
  const formRef = useRef<HTMLFormElement>(null)
  // Guard against empty memberEmail: an empty string would match an empty input, unlocking Confirm.
  const emailMatches = memberEmail.trim().length > 0 && typedEmail.trim().toLowerCase() === memberEmail.trim().toLowerCase()
  const canConfirm = emailMatches && !loading

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (canConfirm) onConfirm()
  }

  return (
    <Modal open onOpenChange={(nextOpen) => !nextOpen && !loading && onClose()}>
      <ModalContainer width="md" height="max-lg" stacked={stacked} preventDismiss={loading} className="flex flex-col">
        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-6">
          <div className="flex flex-col gap-1.5">
            <ModalTitle className="font-rethink-sans text-xl font-semibold leading-7 text-brand-neutral-950">Remove member</ModalTitle>
            <ModalDescription className="break-all text-base font-semibold text-brand-neutral-600">{memberEmail}</ModalDescription>
          </div>

          <div className="flex flex-col gap-4">
            <p className="text-base text-brand-neutral-950">They&apos;ll lose access immediately. Removing them also affects their work:</p>
            <ul className="flex flex-col gap-3">
              <li className="flex gap-1.5">
                <MatterBriefcase className="mt-1 size-[18px] shrink-0 text-brand-neutral-600" aria-hidden />
                <span className="text-base leading-6 text-brand-neutral-950">Their matters transfer to you, including any created before they joined.</span>
              </li>
              <li className="flex gap-1.5">
                <Offline className="mt-1 size-[18px] shrink-0 text-brand-neutral-600" aria-hidden />
                <span className="text-base leading-6 text-brand-neutral-950">
                  Any connected DMS folder will stop syncing. Files already imported into matters stay in place.
                </span>
              </li>
              <li className="flex gap-1.5">
                <TrashDelete className="mt-1 size-[18px] shrink-0 text-red-800" aria-hidden />
                <span className="text-base leading-6 text-brand-neutral-950">Their AI chats on transferred matters are hidden and no longer accessible.</span>
              </li>
            </ul>
          </div>

          <form ref={formRef} onSubmit={handleSubmit}>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="removal-confirm-email" className="text-sm text-brand-neutral-600">
                Type the member&apos;s email to confirm removal
              </label>
              <input
                id="removal-confirm-email"
                type="text"
                value={typedEmail}
                onChange={(e) => setTypedEmail(e.target.value)}
                placeholder={memberEmail}
                disabled={loading}
                autoComplete="off"
                className="h-11 w-full rounded border-0 px-3 py-2 text-sm ring-1 ring-inset ring-brand-neutral-400 focus:ring-inset focus:ring-brand-400 focus:outline-hidden disabled:cursor-not-allowed disabled:bg-brand-neutral-100 disabled:text-brand-neutral-700"
              />
            </div>
          </form>
        </div>

        <div className="shrink-0 p-6">
          <ModalFooterWithActions
            type={ModalFooterWithActionsType.DESTRUCTIVE}
            inlineMessage={error ? { variant: 'error', children: error } : undefined}
            secondaryAction={{ label: 'Cancel', onClick: onClose, disabled: loading }}
            primaryAction={{ label: 'Confirm', onClick: () => formRef.current?.requestSubmit(), loading, disabled: !canConfirm }}
          />
        </div>
      </ModalContainer>
    </Modal>
  )
}
